import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "../../assets/logo.svg";
import AuthModalManager from "../Model/AuthModalManager";

import { openSignInModal } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import ProfileIcon from "../Profile/ProfileIcon";
import UserInfo from "../Form/UserInfo";

import { logout } from "../../store";
import ProfilePopup from "../Profile/ProfilePopup";

const NavLink = ({
  to,
  children,
  className = "whitespace-nowrap md:text-[12px] lg:text-[18px]",
  variant = "link",
  onClick,
  scrollToId = "",
  activeSection = "",
}) => {
  const location = useLocation();

  const handleClick = (e) => {
    if (scrollToId) {
      e.preventDefault();
      const section = document.getElementById(scrollToId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (onClick) onClick();
  };

  const baseStyles = {
    link: "text-green hover:text-primary transition ",
    buttonPrimary:
      "flex items-center justify-center text-white-text rounded-md bg-[var(--primary-orange-color)]",
    buttonSecondary:
      "flex items-center justify-center text-white-text rounded-md bg-gradient-purple shadow-custom-blue",
  };

  const isRouteActive = (() => {
    if (variant !== "link") return false;
    if (location.pathname === "/" && scrollToId) {
      return activeSection === scrollToId;
    }
    return !scrollToId && location.pathname === to;
  })();

  return (
    <Link
      to={scrollToId ? "#" : to}
      onClick={handleClick}
      className={`
        ${baseStyles[variant] || ""}
        ${className}
        ${
          isRouteActive && variant === "link"
            ? "border-b-2 border-[var(--primary-orange-color)]"
            : ""
        }
      `.trim()}
    >
      {children}
    </Link>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const togglePopup = () => setIsPopupOpen((prev) => !prev);
  const closePopup = () => setIsPopupOpen(false);

  const dispatch = useDispatch();
  const { isSignInModalOpen, isAuthenticated, user } = useSelector(
    (state) => state.auth
  );

  const name = `${user?.firstName || ""} ${
    user?.lastName?.split(" ")[0] || ""
  }`.trim();

  // handle show sign in
  const handleShowSignIn = () => {
    dispatch(openSignInModal());
  };

  const handleAddReviews = () => {
    navigate("/review");
  };

  const handleUserProfile = () => {
    navigate("/profile");
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleSectionLink = (sectionId) => {
    if (location.pathname !== "/") {
      navigate("/", { replace: false });
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) section.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const sections = [
      "home",
      "aboutus",
      "services",
      "lifeatroselea",
      "contactus",
    ];
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.4,
    };
    const observer = new IntersectionObserver((entries) => {
      let mostVisibleSection = null;
      let maxRatio = 0;
      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxRatio) {
          mostVisibleSection = entry.target.id;
          maxRatio = entry.intersectionRatio;
        }
      });
      if (mostVisibleSection) setActiveSection(mostVisibleSection);
    }, observerOptions);

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("#userDropdown")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <>
      {isSignInModalOpen && <AuthModalManager />}

      <div
        className={`fixed top-0 z-50 flex justify-center w-full transition-all duration-300 ${
          scrolled ? "bg-white/50 backdrop-blur-md shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="flex justify-between items-center max-w-screen-xl w-11/12 h-[60px]">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center">
            <img
              src={Logo}
              alt="Roselea Logo"
              loading="lazy"
              decoding="async"
              fetchpriority="high"
              className="w-[200px] h-[50px] sm:w-[258px] sm:h-[50px] lg:w-[268px] lg:h-[60px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden w-3/4 font-semibold md:flex md:items-center md:justify-around md:space-x-8 lg:space-x-12 xl:space-x-18 font-poppins ">
            <div className="flex w-4/5 gap-3 lg:w-2/3 lg:gap-4 xl:gap-10 ">
              <NavLink
                to="/"
                onClick={() => handleSectionLink("home")}
                activeSection={activeSection}
              >
                Home
              </NavLink>
              <NavLink to="/about">About Us</NavLink>
              <NavLink to="/services" activeSection={activeSection}>
                Services
              </NavLink>
              <NavLink to="/life-at-roselea">Life At Roselea</NavLink>
              <NavLink to="/contact-us">Contact Us</NavLink>
            </div>
            <div className="flex items-center justify-around relative">
              {isAuthenticated ? (
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  id="userDropdown"
                  onClick={togglePopup}
                >
                  <ProfileIcon
                    onAddReviews={handleAddReviews}
                    onUserProfile={handleUserProfile}
                    onLogout={handleLogout}
                    isOpen={isPopupOpen} // Controlled state
                    onClose={closePopup}
                    size="w-10 h-10"
                  />
                  <UserInfo userName={name} />
                </div>
              ) : (
                <NavLink
                  to="#"
                  variant="buttonPrimary"
                  className="font-poppins rounded-xl font-semibold text-level-7 xl:text-level-5 text-center h-[40px] md:h-[35px] xl:h-[45px] w-[65px] md:w-[85px] xl:w-[115px]"
                  onClick={handleShowSignIn}
                >
                  Login
                </NavLink>
              )}

              {/* Render ProfilePopup here to control its position relative to this div */}
              {isPopupOpen && (
                <ProfilePopup
                  isOpen={isPopupOpen}
                  onClose={closePopup}
                  onAddReviews={handleAddReviews}
                  onUserProfile={handleUserProfile}
                  onLogout={handleLogout}
                />
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-dark" />
              ) : (
                <Menu className="w-6 h-6 text-dark" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`
          fixed inset-0 z-40 top-[80px]
          flex flex-col justify-between
          bg-background
          px-4 py-6
          md:hidden
          font-inter font-semibold
          transition-all duration-300 ease-in-out overflow-y-hidden 
          ${isOpen ? "opacity-100 visible " : "opacity-0 invisible"}
        `}
      >
        {/* Top Group */}
        <div className="mt-4 space-y-4">
          <NavLink
            to="/"
            className="block px-3 py-2 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            Home
          </NavLink>
          <NavLink
            to="/about"
            className="block px-3 py-2 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            About Us
          </NavLink>
          <NavLink
            to="/services"
            className="block px-3 py-2 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            Services
          </NavLink>
          <NavLink
            to="/life-at-roselea"
            className="block px-3 py-2 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            Life At Roselea
          </NavLink>
          <NavLink
            to="/contact-us"
            className="block px-3 py-2 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            Contact Us
          </NavLink>
        </div>

        {/* Bottom Group */}
        {isAuthenticated ? (
          <div className="space-y-3 font-poppins">
            <NavLink
              to="#"
              variant="buttonPrimary"
              className="w-full h-12 rounded-xl"
              onClick={handleAddReviews}
            >
              Add Reviews
            </NavLink>
            <NavLink
              to="#"
              variant="buttonPrimary"
              className="w-full h-12 rounded-xl"
              onClick={handleUserProfile}
            >
              User Profile
            </NavLink>
            <NavLink
              to="#"
              variant="buttonPrimary"
              className="w-full h-12 rounded-xl"
              onClick={handleLogout}
            >
              Logout
            </NavLink>
          </div>
        ) : (
          <div className="space-y-3 font-poppins">
            <NavLink
              to="#"
              variant="buttonPrimary"
              className="w-full h-12 rounded-xl"
              onClick={handleShowSignIn}
            >
              Login
            </NavLink>
          </div>
        )}
      </div>
    </>
  );
};

export default Navbar;
